import filter from "lodash/filter";
import has from "lodash/has";

import { useClientsStore } from "../clients";

const API_NAME = "rota";
const API_VERSION = "/v1";
const CONTENT_TYPE = "application/vnd.coactive.client+json";

const MAX_PAGE_SIZE = 250;

const modulestate = () => ({
  // The state must return a function to make the module reusable.
});
const mutations = {};

const actions = {
  async getClient(context, params) {
    // the 'get client' (singular) API endpoint returns the same data as the get clients endpoint,
    // so we can use the cached data for speed
    const piniaStore = useClientsStore();
    const clients = await piniaStore.getAllClients();
    // search clients
    const client = clients.find((a) => a.id === params.id);
    if (client) {
      return client;
    }
    // fallback to searching the API
    return this.$api.get(API_NAME, `${API_VERSION}/clients/${params.id}`);
  },
  async getClients(context, payload) {
    const myInit = {};

    // payload contains filter/sort/pagination options.
    if (payload) {
      myInit.queryStringParameters = {
        page: payload.page,
        pageSize: payload.pageSize ? payload.pageSize : MAX_PAGE_SIZE,
      };

      if (has(payload, "sortBy")) {
        let sortField = payload.sortBy;
        if (sortField === "dateCreated") {
          sortField = "_id";
        }
        let sortOrder = 1;
        if (has(payload, "sortDesc") && payload.sortDesc === true) {
          sortOrder = -1;
        }
        myInit.queryStringParameters.sort = JSON.stringify({
          [sortField]: sortOrder,
        });
      }

      if (has(payload, "filter")) {
        myInit.queryStringParameters.filter = JSON.stringify(payload.filter);
      }
    }

    return this.$api
      .get(API_NAME, `${API_VERSION}/clients`, myInit)
      .then((response) => {
        if (has(response, "_embedded.clients")) {
          return response;
        }
        return false;
      });
  },
  async createClient(context, client) {
    const myInit = {
      headers: {
        Accept: CONTENT_TYPE,
      },
      body: client,
    };
    const piniaStore = useClientsStore();
    piniaStore.localStorageInvalidateCache("getAllClients");
    return this.$api.post(API_NAME, `${API_VERSION}/clients`, myInit);
  },
  async updateClient(context, params) {
    const myInit = {
      headers: {
        Accept: CONTENT_TYPE,
      },
      body: params.changes,
    };
    const piniaStore = useClientsStore();
    piniaStore.localStorageInvalidateCache("getAllClients");
    return this.$api.patch(
      API_NAME,
      `${API_VERSION}/clients/${params.id}`,
      myInit,
    );
  },
  async addClientHours(context, params) {
    const myInit = {
      headers: {
        Accept: CONTENT_TYPE,
      },
      body: params.hours,
    };
    const piniaStore = useClientsStore();
    piniaStore.localStorageInvalidateCache("getAllClients");
    return this.$api.post(
      API_NAME,
      `${API_VERSION}/clients/${params.id}/hours`,
      myInit,
    );
  },
  async addClientEscalation(context, params) {
    const myInit = {
      headers: {
        Accept: CONTENT_TYPE,
      },
      body: params.changes,
    };
    const piniaStore = useClientsStore();
    piniaStore.localStorageInvalidateCache("getAllClients");
    return this.$api.post(
      API_NAME,
      `${API_VERSION}/clients/${params.id}/careEscalationHistory`,
      myInit,
    );
  },
  async deleteClient(context, payload) {
    const piniaStore = useClientsStore();
    piniaStore.localStorageInvalidateCache("getAllClients");

    return this.$api.del(API_NAME, `${API_VERSION}/clients/${payload.id}`);
  },
  async getClientsAtLocationForClientSelection(context, location) {
    return filter(
      await context.dispatch("getAllClients"),
      (client) => client.location.locationId === location,
    );
  },
  async getAllClients() {
    const piniaStore = useClientsStore();
    return piniaStore.getAllClients();
  },

  async getNowNext(context, payload) {
    const api = this.$api;
    if (payload.doNotCatchIfError) {
      api.doNotCatchIfError();

      delete payload.doNotCatchIfError;
    }
    const myInit = {
      queryStringParameters: {
        ...payload,
      },
    };
    const response = await api.get(
      API_NAME,
      `${API_VERSION}/clients/now-next`,
      myInit,
    );
    return response;
  },
};

export default {
  namespaced: true,
  actions,
  mutations,
  state: modulestate,
};
